import classNames from "classnames";
import { BroadcastTracker } from "components/BroadcastCall/BroadcastTracker";
import { CallHistory } from "components/chat/CallHistory";
import { ChatExpandFrame } from "components/chat/ChatExpandFrame";
import { ChatExpandToggle } from "components/chat/ChatExpandToggle";
import { PageNotFound } from "components/design/PageNotFound";
import { Chat } from "components/chat/Chat";
import { ChatSettings } from "components/ChatSettings/ChatSettings";
import DashboardContainer from "components/dashboard/DashboardContainer";
import { BlurFade } from "components/design/BlurFade";
import ConditionalToolTip from "components/design/conditionalToolTip";
import Toggle from "components/design/toggle";
import { Button } from "components/DesignSystem/Button/Button";
import { Header } from "components/DesignSystem/Header/Header";
import { ConditionalLink } from "components/conditionalLink";
import Tab from "components/DesignSystem/Tab/Tab";
import { Tag } from "components/DesignSystem/Tag/Tag";
import { MegaPhone } from "components/icons/Chat/MegaPhone";
import { Refresh } from "components/icons/Refresh";
import { DocumentPreviewModal } from "components/PreviewModal";
import { UnifiedBroadcast } from "components/UnifiedBroadcast/UnifiedBroadcast";
import { AI_CHANNEL, ANNOUNCEMENTS_CHANNEL } from "constants/chatType";
import { RANDOM_DATE_FOR_CHANNELS_LAST_MESSAGE } from "constants/date";
import { withChatFilterContext } from "contexts/ChatFilterContext";
import { EntityChatFilterProvider } from "contexts/EntityChatFilterContext";
import { useConstructInternalLink } from "hooks/useConstructInternalLink";
import { useIsSmallScreen } from "hooks/useIsSmallScreen";
import { useModal } from "hooks/useModal";
import { usePageTitle } from "hooks/usePageTitle";
import { useDisableContentTopPadding } from "hooks/useDisableContentTopPadding";
import { SlackIntegration } from "components/SlackIntegration";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useStreamFilterQuery } from "hooks/useStreamFilterQuery";
import { useUnrepliedCount } from "hooks/useUnrepliedCount";
import authContext from "jwt_context&axios/authContext";
import {
  ComponentProps,
  MouseEvent,
  useContext,
  useEffect,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Redirect,
  Route,
  Switch,
  useHistory,
  useLocation,
  useParams,
  useRouteMatch,
} from "react-router-dom";
import { collapseChat } from "store/slices/chatExpanded";
import { setScrollToMessageId } from "store/slices/messageToScrollTo";
import { RootState } from "store/store";
import { useChatContext } from "stream-chat-react";

const UnrepliedChannelCount = () => {
  const { count, isLoading } = useUnrepliedCount();

  return (
    <ConditionalToolTip condition={count > 99} text={count}>
      <div className="t-group t-ml-1 t-w-11">
        <Tag icon={false} tagType="gray" rounded>
          <BlurFade
            className={classNames({
              "group-hover:t-hidden": !isLoading,
              "t-hidden": isLoading,
            })}
          >
            {count > 99 ? "99+" : count}
          </BlurFade>
          <div className="t-flex t-items-center t-justify-center t-w-11">
            <div
              className={classNames({
                "t-animate-spin-forward": isLoading,
                "group-hover:t-block t-hidden": !isLoading,
              })}
            >
              <Refresh size="12" />
            </div>
          </div>
        </Tag>
      </div>
    </ConditionalToolTip>
  );
};

const UnrepliedCount = () => {
  const [reset, setReset] = useState(0);

  const onRefresh = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setReset(Math.random());
  };

  return (
    <button onClick={onRefresh} className="all:unset">
      <UnrepliedChannelCount key={reset} />
    </button>
  );
};

const ChatUI = (
  props: ComponentProps<typeof Chat> & {
    path: string;
    sortByNewest?: boolean;
    isMyMessages?: boolean;
  }
) => {
  const { setActiveChannel, client, channel } = useChatContext();
  const { channelId, messageId } = useParams<{
    channelId?: string;
    messageId?: string;
  }>();
  const history = useHistory();
  const dispatch = useDispatch();
  const { search } = useLocation();
  const filterQuery = useStreamFilterQuery();

  const onChannelSelect = (newChannelId: string, messageId?: string) => {
    let newPath = `${props.path}/${newChannelId}`;

    if (messageId) {
      newPath = `${newPath}/${messageId}`;
    }

    if (search) {
      newPath = `${newPath}${search}`;
    }

    history.push(newPath);
    return;
  };

  useEffect(() => {
    if (channelId) {
      const channelData = client.channel("messaging", channelId, {});
      setActiveChannel(channelData);
    }
  }, [channelId, client]);

  useEffect(() => {
    if (messageId && channel?.id === channelId) {
      dispatch(setScrollToMessageId(messageId));
    }
  }, [channel?.id, channelId, messageId]);

  const filters = {
    ...filterQuery,
    ...props.filters,
  };

  return (
    <Chat
      channelId={channelId}
      onChannelSelect={onChannelSelect}
      {...props}
      filters={filters}
      isMyMessages={props.isMyMessages}
    />
  );
};

const ChatHome = ({ path }: { path: string }) => {
  const { client } = useChatContext();

  const { isAdmin, isCpa, isCustomer, isUserTypeForeignCA, isForeignCA } =
    useRoleBasedView();
  const chatSearchValue = useSelector(
    (state: RootState) => state.chatSearch.value
  );
  const { showAIChannels } = useSelector(
    (state: RootState) => state.chatSearch
  );
  const isFCA = isUserTypeForeignCA || isForeignCA;

  const filterEmptyChatForAdmin =
    (isAdmin || isCpa || isFCA) && !chatSearchValue && !showAIChannels
      ? {
          last_non_broadcast_message_at: {
            $gte: RANDOM_DATE_FOR_CHANNELS_LAST_MESSAGE,
          },
        }
      : {};

  return (
    <ChatUI
      path={path}
      filters={{
        type: "messaging",
        ...(isCustomer
          ? {}
          : {
              custom_type: {
                $nin: [ANNOUNCEMENTS_CHANNEL, AI_CHANNEL],
              },
            }),

        members: { $in: [client.userID!] },
        ...filterEmptyChatForAdmin,
      }}
      sortByNewest={true}
      showEntityFilters={isCustomer}
    />
  );
};

const MyMessage = ({ path }: { path: string }) => {
  const { authtoken } = useContext(authContext);
  const { client, setActiveChannel } = useChatContext();
  const { isAdmin, isCpa, isUserTypeForeignCA, isForeignCA } =
    useRoleBasedView();
  const chatSearchValue = useSelector(
    (state: RootState) => state.chatSearch.value
  );

  const isFCA = isUserTypeForeignCA || isForeignCA;

  useEffect(() => {
    setActiveChannel();
  }, []);

  const assignedFilter = {
    $or: [
      { assigned_agent: authtoken.uuid },
      { assigned_preparer: authtoken.uuid },
      { assigned_reviewer: authtoken.uuid },
    ] as [
      { assigned_agent: string },
      { assigned_preparer: string },
      { assigned_reviewer: string }
    ],
  };

  const myMessagesFilters = {
    type: "messaging" as const,
    ...assignedFilter,
    members: { $in: [client.userID!] },
    ...((isAdmin || isCpa || isFCA) && !chatSearchValue
      ? {
          last_non_broadcast_message_at: {
            $gte: RANDOM_DATE_FOR_CHANNELS_LAST_MESSAGE,
          },
        }
      : {}),
  };

  return (
    <ChatUI
      path={path}
      filters={myMessagesFilters}
      sortByNewest={true}
      isMyMessages
    />
  );
};

const Broadcast = ({ path }: { path: string }) => {
  const { client, setActiveChannel } = useChatContext();

  useEffect(() => {
    setActiveChannel();
  }, []);

  return (
    <ChatUI
      path={path}
      filters={{
        type: "messaging",
        custom_type: ANNOUNCEMENTS_CHANNEL,
        members: { $in: [client.userID!] },
      }}
      broadcast
      hideTabBar
    />
  );
};

const BaseChat = () => {
  usePageTitle("Chat");
  useDisableContentTopPadding();
  const { path, url } = useRouteMatch();
  const { pathname } = useLocation();
  const { isAdmin, isCpa, isUserTypeForeignCA, isForeignCA, isCustomer } =
    useRoleBasedView();

  const isFCA = isUserTypeForeignCA || isForeignCA;

  const { authtoken } = useContext(authContext);
  const { link } = useConstructInternalLink();
  const [currentTask, setCurrentTask] = useState({ other_channel_url: "" });
  const [checked, setChecked] = useState(true);
  const [isPrivateChannel, setPrivateChannel] = useState(false);
  const [showToggle, setShowToggle] = useState(false);
  const [showPublicPrivateToggle, setShowPublicPrivateToggle] = useState(true);
  const { client } = useChatContext();
  const chatSearchValue = useSelector(
    (state: RootState) => state.chatSearch.value
  );
  const { showAIChannels } = useSelector(
    (state: RootState) => state.chatSearch
  );
  const unifiedBroadcast = useModal();
  const isSmallScreen = useIsSmallScreen();
  const dispatch = useDispatch();
  const isChatExpanded = useSelector(
    (state: RootState) => state.chatExpanded.isExpanded
  );

  useEffect(() => {
    return () => {
      dispatch(collapseChat());
    };
  }, [dispatch]);

  const setChatType = (toggleValue: boolean) => {
    setChecked(toggleValue);
    setPrivateChannel(toggleValue);
  };

  useEffect(() => {
    if (pathname.includes("callhistory")) {
      setShowPublicPrivateToggle(false);
    } else {
      setShowPublicPrivateToggle(true);
    }
  }, [pathname]);

  const filterEmptyChatForAdmin =
    (isAdmin || isCpa || isFCA) && !chatSearchValue && !showAIChannels
      ? {
          last_non_broadcast_message_at: {
            $gte: RANDOM_DATE_FOR_CHANNELS_LAST_MESSAGE,
          },
        }
      : {};

  const isChatHome = [
    "unreplied",
    "mymessages",
    "broadcast",
    "callhistory",
    "settings",
  ].every((v) => !pathname.includes(v));

  const broadcastButton = isAdmin && (
    <div>
      <Button size="small" onClick={unifiedBroadcast.open}>
        <div className="t-flex t-gap-1 t-items-center">
          <MegaPhone className="t-text-text-60" />
          Broadcast
        </div>
      </Button>
      <UnifiedBroadcast {...unifiedBroadcast} />
    </div>
  );

  const chatTabs = (
    <Tab.Root value="MATCH" className="md:t-w-auto t-w-full">
      <div className="chatHeader t-flex t-items-center t-justify-between t-gap-8 t-p-0">
        <Tab.List className="!t-mb-0 !t-border-b-0 !t-pt-5">
          {isCpa && (
            <Tab.NavTrigger
              to={`${url}/${link("unreplied")}`}
              value={`${url}/unreplied`}
            >
              Unreplied
              <UnrepliedCount />
            </Tab.NavTrigger>
          )}
          {isCpa && (
            <Tab.NavTrigger
              to={link(url)}
              useAutoSelect={false}
              value={isChatHome ? "MATCH" : "UNMATCH"}
            >
              <div className="t-min-w-max">All Chats</div>
            </Tab.NavTrigger>
          )}
          {isAdmin && (
            <>
              <Tab.NavTrigger
                to={`${url}/${link("mymessages")}`}
                value={`${url}/mymessages`}
              >
                <div className="t-min-w-max">My Chats</div>
              </Tab.NavTrigger>
              <Tab.NavTrigger
                to={`${url}/${link("callhistory")}`}
                value={`${url}/callhistory`}
              >
                <div className="t-min-w-max">Call Log</div>
              </Tab.NavTrigger>
              <Tab.NavTrigger
                to={`${url}/${link("broadcast")}`}
                value={`${url}/broadcast`}
              >
                Announcements
              </Tab.NavTrigger>
              <Tab.NavTrigger
                to={`${url}/${link("settings")}`}
                value={`${url}/settings`}
              >
                Settings
              </Tab.NavTrigger>
              <Tab.NavTrigger
                to={`${url}/${link("broadcasts")}`}
                value={`${url}/broadcasts`}
              >
                Broadcasts
              </Tab.NavTrigger>
            </>
          )}
        </Tab.List>
        {authtoken.is_any_service_user &&
          currentTask?.other_channel_url &&
          showPublicPrivateToggle && (
            <div
              className={classNames("t-block", {
                "sm:t-hidden": !showToggle,
                "sm:t-block": showToggle,
              })}
            >
              <span className="t-flex t-w-[150px] t-items-center t-justify-between t-font-medium t-leading-[18px]">
                <span
                  className={classNames({
                    "t-text-purple": !checked,
                  })}
                >
                  Public
                </span>
                <Toggle setToggleValue={setChatType} checked={checked}></Toggle>
                <span
                  className={classNames({
                    "t-text-purple": checked,
                  })}
                >
                  Private
                </span>
              </span>
            </div>
          )}
      </div>
    </Tab.Root>
  );

  const chatTitle = (
    <ConditionalLink
      to={isSmallScreen ? link(url) : undefined}
      className="t-block t-text-text-60"
    >
      Chat
    </ConditionalLink>
  );

  const adminHeaderProps = {
    className:
      "t-border-x-0 t-border-b t-border-t-0 t-border-solid t-border-neutral-0",
    title: chatTitle,
    right: (
      <div className="t-flex t-gap-2">
        <span className="t-flex t-flex-row t-items-center t-gap-2">
          {broadcastButton}
          <ChatExpandToggle />
        </span>
      </div>
    ),
  };

  const slackConnect = !authtoken.is_any_service_user ? (
    <SlackIntegration hideOnceAdded floating reverse={undefined} isChat />
  ) : null;

  const defaultHeaderProps = {
    title: chatTitle,
    right: (
      <div className="t-flex t-gap-2 t-items-center">
        {slackConnect}
        {broadcastButton}
        <ChatExpandToggle />
      </div>
    ),
  };

  const headerProps = isAdmin ? adminHeaderProps : defaultHeaderProps;

  const hasChatTabs = isAdmin || isCpa;

  return (
    <ChatExpandFrame>
      <DashboardContainer className="t-h-full">
        <DashboardContainer.Header
          // On mobile the channel list and the open thread render their own headers
          className={classNames("t-z-header t-hidden", {
            "md:t-block": !isChatExpanded,
          })}
        >
          <Header v2 {...headerProps} />
        </DashboardContainer.Header>
        <DashboardContainer.Content className="t-flex t-flex-col t-min-h-0 !t-overflow-hidden">
          {hasChatTabs && (
            // Tabs eat too much vertical space on mobile, desktop only
            <div className="t-hidden md:t-block t-shrink-0 t-border-x-0 t-border-b t-border-t-0 t-border-solid t-border-neutral-0 t-px-5">
              {chatTabs}
            </div>
          )}
          <div className="t-min-h-0 t-flex-1">
            <Switch>
              <Route
                exact
                path={[
                  `${path}/mymessages/`,
                  `${path}/mymessages/:channelId/`,
                  `${path}/mymessages/:channelId/:messageId`,
                ]}
              >
                <MyMessage path={`${url}/mymessages`} />
              </Route>
              <Route exact path={`${path}/callhistory/`}>
                {isAdmin ? <CallHistory /> : <PageNotFound />}
              </Route>
              <Route
                exact
                path={[
                  `${path}/unreplied/`,
                  `${path}/unreplied/:channelId/`,
                  `${path}/unreplied/:channelId/:messageId`,
                ]}
              >
                <ChatUI
                  path={`${url}/unreplied`}
                  filters={{
                    type: "messaging",
                    needs_reply: true,
                    members: { $in: [client.userID!] },
                    ...filterEmptyChatForAdmin,
                  }}
                  unreplied
                  sortByNewest={false}
                />
              </Route>
              <Route
                exact
                path={[
                  `${path}/broadcast/`,
                  `${path}/broadcast/:channelId/`,
                  `${path}/broadcast/:channelId/:messageId`,
                ]}
              >
                <Broadcast path={`${url}/broadcast`} />
              </Route>
              <Route exact path={`${path}/call-broadcasts/`}>
                <Redirect to={`${path}/broadcasts/`} />
              </Route>
              <Route exact path={`${path}/broadcasts/`}>
                <BroadcastTracker />
              </Route>
              <Route exact path={`${path}/settings/`}>
                <ChatSettings />
              </Route>
              <Route
                exact
                path={[
                  path,
                  `${path}/:channelId/`,
                  `${path}/:channelId/:messageId`,
                ]}
              >
                <ChatHome path={url} />
              </Route>
            </Switch>
          </div>
          <DocumentPreviewModal />
        </DashboardContainer.Content>
      </DashboardContainer>
    </ChatExpandFrame>
  );
};

const ChatPageWithEntityFilters = () => (
  <EntityChatFilterProvider>
    <BaseChat />
  </EntityChatFilterProvider>
);

export const ChatPage = withChatFilterContext(ChatPageWithEntityFilters);
